import {
  EllipsisOutlined,
  // ExperimentOutlined, // TEMP-MOCK-TRACK UI — commented out for production
  ExportOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import {
  // Alert, // TEMP-MOCK-TRACK UI — commented out for production
  Dropdown,
  Input,
  Menu,
  Select,
  Spin,
  Tooltip,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import * as dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import orderApi from '../../apis/order';
import shippoApi from '../../apis/shippo';
// TEMP-MOCK-TRACK UI — commented out for production; restore for Shippo webhook QA.
// import { ShippoMockTrackStatus } from '../../apis/shippo';
import userApi from '../../apis/user';
import {
  OrderProductTypeMap,
  OrderStatus,
  ProductTypeOptions,
  ShipmentStatus,
  LabelFilter,
  TrackingFilter,
  isEligibleForShippoPurchase,
  isNeedsShippoPurchase,
} from '../../constants/order';
import { IUser } from '../../interfaces/UserModel';
import { StatusTag } from '../Order/Order.styled';
import { ButtonType } from '../../constants/style';
import { useHasMounted } from '../../hooks';
import { IOrder, OrderType } from '../../interfaces/Order';
import { PaginationParamsResponse } from '../../interfaces/PaginationParamsResponse';
import { RootState } from '../../reducers';
import { IAuthState } from '../../reducers/auth';
import { Role } from '../../role/role';
import { PageContainer } from '../../styles/Global';
import Button from '../Button/Button';
import PageHeader from '../PageHeader/PageHeader';
import {
  ActionContainer,
  OrderTable,
  SpinContainer,
  ThreeDot,
} from '../Order/Order.styled';

type QueueView =
  | 'needs_purchase'
  | 'purchased'
  | 'in_transit'
  | 'delivered'
  | 'problems';

const QueueOptions = [
  { label: 'Cần Tracking (chưa mua)', value: 'needs_purchase' },
  { label: 'Đã mua label', value: 'purchased' },
  { label: 'In transit', value: 'in_transit' },
  { label: 'Delivered', value: 'delivered' },
  { label: 'Problems', value: 'problems' },
];

const StatusOptions = [
  { label: 'All statuses', value: '' },
  { label: 'Pending', value: OrderStatus.PENDING },
  { label: 'Processing', value: OrderStatus.PROCESSING },
  { label: 'Shipped', value: OrderStatus.SHIPPED },
  { label: 'Reshipped', value: OrderStatus.RESHIPPED },
  { label: 'Cancelled', value: OrderStatus.CANCELLED },
];

// TEMP-MOCK-TRACK UI — commented out for production; restore for Shippo webhook QA.
// const MockTrackOptions: { label: string; value: ShippoMockTrackStatus }[] = [
//   { label: 'PRE_TRANSIT', value: 'PRE_TRANSIT' },
//   { label: 'TRANSIT', value: 'TRANSIT' },
//   { label: 'DELIVERED', value: 'DELIVERED' },
//   { label: 'RETURNED', value: 'RETURNED' },
//   { label: 'FAILURE', value: 'FAILURE' },
//   { label: 'UNKNOWN', value: 'UNKNOWN' },
// ];

const emptyPagination = (
  page = 1,
  perPage = 20,
): PaginationParamsResponse<IOrder> => ({
  items: [],
  page,
  perPage,
  total: 0,
  pageCount: 0,
});

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin: 10px 0 20px;
`;

const SelectionHint = styled.span`
  color: var(--color-light-grey);
  font-size: 13px;
`;

// TEMP-MOCK-TRACK UI — commented out for production; restore for Shippo webhook QA.
// const TempTestBar = styled.div`
//   display: flex;
//   flex-wrap: wrap;
//   align-items: center;
//   gap: 12px;
//   margin: 0 0 16px;
//   padding: 12px 14px;
//   border: 1px dashed #d9a514;
//   background: #fffbe6;
// `;

const ShippoTracking = () => {
  const hasMounted = useHasMounted();
  const { authUser } = useSelector(
    (state: RootState) => state.auth as IAuthState,
  );
  const isAdmin = authUser?.role === Role.ADMIN;

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [queue, setQueue] = useState<QueueView>('needs_purchase');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sellerUsername, setSellerUsername] = useState<string[]>();
  const [sellers, setSellers] = useState<IUser[]>();
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  // TEMP-MOCK-TRACK UI — commented out for production; restore for Shippo webhook QA.
  // const [mockStatus, setMockStatus] =
  //   useState<ShippoMockTrackStatus>('TRANSIT');
  const [itemsPagination, setItemsPagination] =
    useState<PaginationParamsResponse<IOrder>>(emptyPagination());
  const canBuySelectedShippoLabels = useMemo(() => {
    if (selectedRowKeys.length === 0) return false;
    const selectedOrders = itemsPagination.items.filter((order) =>
      selectedRowKeys.includes(order?.id || ''),
    );
    if (selectedOrders.length !== selectedRowKeys.length) return false;
    return selectedOrders.every(isEligibleForShippoPurchase);
  }, [selectedRowKeys, itemsPagination]);

  const fetchOrders = async (
    page = currentPage,
    pageSize = perPage,
    searchValue = search,
    queueValue = queue,
  ) => {
    try {
      setLoading(true);
      const query: Record<string, any> = {
        search: searchValue || undefined,
        status: statusFilter || undefined,
        seller: sellerUsername?.length ? sellerUsername : undefined,
      };

      // Purchased-type queues match Shippo-bought labels only (not EasyPost).
      if (queueValue === 'needs_purchase') {
        query.tracking = TrackingFilter.NeedsShippoPurchase;
      } else if (queueValue === 'purchased') {
        query.labelFilter = LabelFilter.ShippoBought;
      } else if (queueValue === 'in_transit') {
        query.labelFilter = LabelFilter.ShippoBought;
        query.shipmentStatus = ShipmentStatus.IN_TRANSIT;
      } else if (queueValue === 'delivered') {
        query.labelFilter = LabelFilter.ShippoBought;
        query.shipmentStatus = ShipmentStatus.DELIVERED;
      } else {
        query.labelFilter = LabelFilter.ShippoBought;
      }

      const res = await orderApi.getOrders(query, {
        page,
        perPage: pageSize,
      });
      let items = res.items || [];

      if (queueValue === 'problems') {
        const problemStatuses = new Set([
          ShipmentStatus.FAILURE,
          ShipmentStatus.RETURN_TO_SENDER,
          ShipmentStatus.ERROR,
          ShipmentStatus.CANCELLED,
        ]);
        items = items.filter((o) =>
          problemStatuses.has(o.shipmentStatus as ShipmentStatus),
        );
      }

      // No client-side type filter needed: needs_purchase and the
      // labelFilter=shippo_bought queues are both fully server-filtered.
      setItemsPagination({
        ...res,
        items,
      });
      setSelectedRowKeys([]);
    } catch (e) {
      message.error('Failed to load Shippo tracking orders');
      console.error(e);
      setItemsPagination(emptyPagination(page, pageSize));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasMounted || !isAdmin) return;
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMounted, isAdmin, currentPage, perPage, queue, statusFilter, sellerUsername]);

  useEffect(() => {
    if (!hasMounted || !isAdmin) return;
    userApi
      .listUsers({ all: true })
      .then((res) => setSellers(res.items))
      .catch((e) => {
        console.error('Failed to load sellers', e);
      });
  }, [hasMounted, isAdmin]);

  const purchaseShippoShipment = async (id: string, address: string) => {
    try {
      setActionLoading(true);
      const response = await orderApi.purchaseShippoShipment(id, address);
      message.success('Shippo label purchased successfully!');
      const tmp = cloneDeep(itemsPagination.items);
      const idx = tmp.findIndex((t) => t.id === id);
      if (idx > -1) {
        if (queue === 'needs_purchase') {
          tmp.splice(idx, 1);
        } else {
          tmp[idx] = {
            ...tmp[idx],
            ...response,
          };
        }
        setItemsPagination({ ...itemsPagination, items: tmp });
      } else {
        fetchOrders();
      }
    } catch (e: any) {
      message.error(
        e?.response?.data?.message || 'Failed to purchase Shippo label',
      );
    } finally {
      setActionLoading(false);
    }
  };

  const bulkPurchaseShippoShipments = async () => {
    const orderIds = selectedRowKeys.map(String).filter(Boolean);
    if (!orderIds.length) {
      message.warning('Select at least one order');
      return;
    }
    if (!canBuySelectedShippoLabels) {
      message.warning(
        'Select only Cần Tracking sticker/magnet/ornament orders that do not already have a label',
      );
      return;
    }
    if (orderIds.length > 50) {
      message.warning('You can buy at most 50 Shippo labels at once');
      return;
    }

    try {
      setActionLoading(true);
      const result = await orderApi.bulkPurchaseShippoShipment(orderIds);
      const successCount = result.succeeded?.length || 0;
      const failCount = result.failed?.length || 0;

      if (successCount > 0 && failCount === 0) {
        message.success(`Purchased ${successCount} Shippo label(s)`);
      } else if (successCount > 0 && failCount > 0) {
        message.warning(
          `Purchased ${successCount} label(s); ${failCount} failed`,
        );
        console.warn('Bulk Shippo failures', result.failed);
      } else {
        message.error(
          result.failed?.[0]?.error || 'Failed to purchase Shippo labels',
        );
        console.warn('Bulk Shippo failures', result.failed);
      }

      setSelectedRowKeys([]);
      await fetchOrders();
    } catch (e: any) {
      message.error(
        e?.response?.data?.message || 'Failed to bulk purchase Shippo labels',
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Links Shippo labels purchased directly on the Shippo dashboard to our
  // orders (matched by the tracking number an admin pasted into the order).
  const syncShippoOrders = async () => {
    try {
      setActionLoading(true);
      const result = await shippoApi.syncOrders();
      const linkedCount = result.linked?.length || 0;
      if (linkedCount > 0) {
        const names = result.linked
          .map((l) => l.orderNumber || l.orderId)
          .slice(0, 5)
          .join(', ');
        const more = linkedCount > 5 ? ` +${linkedCount - 5} more` : '';
        message.success(
          `Linked ${linkedCount} order(s): ${names}${more} — see "Đã mua label"`,
          6,
        );
      } else {
        message.info(
          `No orders to link (scanned ${result.scannedTransactions} Shippo transaction(s))`,
        );
      }
      await fetchOrders();
    } catch (e: any) {
      message.error(
        e?.response?.data?.message || 'Failed to sync Shippo orders',
      );
    } finally {
      setActionLoading(false);
    }
  };

  // TEMP-MOCK-TRACK UI (test token only) — commented out for production;
  // restore for Shippo webhook QA.
  // const applyMockTrack = async () => {
  //   if (selectedRowKeys.length !== 1) {
  //     message.warning(
  //       'Select exactly one purchased order to apply mock status',
  //     );
  //     return;
  //   }
  //   const orderId = String(selectedRowKeys[0]);
  //   try {
  //     setActionLoading(true);
  //     const result = await shippoApi.triggerMockTrack(orderId, mockStatus);
  //     message.success(
  //       `Mock ${result.mockTrackingNumber} → ${result.shipmentStatus}`,
  //     );
  //     console.info('Shippo mock track response', result.shippoTrack);
  //     await fetchOrders();
  //   } catch (e: any) {
  //     message.error(
  //       e?.response?.data?.message || 'Failed to trigger Shippo mock track',
  //     );
  //   } finally {
  //     setActionLoading(false);
  //   }
  // };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
    getCheckboxProps: (order: IOrder) => ({
      disabled:
        queue === 'needs_purchase'
          ? !isNeedsShippoPurchase(
              order.trackingReference,
              order.shippingLabelUrl,
              order.shipmentId,
            )
          : false,
    }),
    columnWidth: 48,
  };

  const columns: TableColumnsType<IOrder> = [
    {
      title: 'Order ID',
      dataIndex: 'orderId',
      key: 'orderId',
      width: 160,
      render: (_: any, order: IOrder) => (
        <Link
          to={`/order/${order.id}/edit/${
            OrderProductTypeMap[order.type ?? OrderType.Sticker]
          }`}
        >
          {order.orderId || order.id}
        </Link>
      ),
    },
    {
      title: 'Seller',
      key: 'seller',
      width: 120,
      render: (_: any, order: IOrder) => order.seller?.username || '—',
    },
    {
      title: 'Store',
      key: 'store',
      width: 120,
      render: (_: any, order: IOrder) => order.store?.name || '—',
    },
    {
      title: 'Product',
      key: 'type',
      width: 120,
      render: (_: any, order: IOrder) =>
        ProductTypeOptions.find(
          (p) =>
            p.value ===
            OrderProductTypeMap[order.type ?? OrderType.Sticker],
        )?.label ||
        order.type ||
        'Sticker',
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      width: 280,
      render: (address: string) => (
        <div style={{ whiteSpace: 'pre-wrap' }}>{address}</div>
      ),
    },
    {
      title: 'Tracking',
      dataIndex: 'trackingReference',
      key: 'trackingReference',
      width: 180,
      render: (_: any, order: IOrder) =>
        order.trackingUrl ? (
          <a href={order.trackingUrl} target="_blank" rel="noopener noreferrer">
            {order.trackingReference}
          </a>
        ) : (
          <span>{order.trackingReference}</span>
        ),
    },
    {
      title: 'Shipment Status',
      dataIndex: 'shipmentStatus',
      key: 'shipmentStatus',
      width: 140,
      render: (status: string) => status || '—',
    },
    {
      title: 'Service',
      key: 'shippingService',
      width: 160,
      render: (_: any, order: IOrder) => {
        const label = [order.shippingCarrier, order.shippingService]
          .filter(Boolean)
          .join(' - ');
        return label ? (
          <Tooltip title={label}>
            <div className="ellipsis">{label}</div>
          </Tooltip>
        ) : (
          '—'
        );
      },
    },
    {
      title: 'Label Fee',
      key: 'labelFee',
      width: 100,
      render: (_: any, order: IOrder) =>
        order.labelFee != null ? `$${Number(order.labelFee).toFixed(2)}` : '—',
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      render: (_: any, order: IOrder) => (
        <StatusTag
          $pending={order.status === OrderStatus.PENDING}
          $process={order.status === OrderStatus.PROCESSING}
          $shipped={order.status === OrderStatus.SHIPPED}
          $reshipped={order.status === OrderStatus.RESHIPPED}
          $cancel={order.status === OrderStatus.CANCELLED}
          $fontSize={12}
        >
          <span style={{ fontSize: '20px' }}>•</span> {order.status}
        </StatusTag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (createdAt: string) =>
        createdAt ? dayjs.default(createdAt).format('DD MMM YYYY') : '',
    },
    {
      title: '',
      key: 'actions',
      width: 64,
      fixed: 'right' as const,
      render: (_: any, order: IOrder) => {
        const menu = (
          <Menu>
            {isNeedsShippoPurchase(
              order.trackingReference,
              order.shippingLabelUrl,
              order.shipmentId,
            ) && (
              <Menu.Item
                key="buy"
                icon={<ShoppingCartOutlined />}
                onClick={() => {
                  if (order.id && order.address) {
                    purchaseShippoShipment(order.id, order.address);
                  } else {
                    message.error('Order address is required');
                  }
                }}
              >
                Buy Shippo Label
              </Menu.Item>
            )}
            {!!order.shippingLabelUrl && (
              <Menu.Item key="label">
                <a
                  href={order.shippingLabelUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Label
                </a>
              </Menu.Item>
            )}
            {!!order.trackingUrl && (
              <Menu.Item key="track" icon={<ExportOutlined />}>
                <a
                  href={order.trackingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open Tracking
                </a>
              </Menu.Item>
            )}
            <Menu.Item key="edit">
              <Link
                to={`/order/${order.id}/edit/${
                  OrderProductTypeMap[order.type ?? OrderType.Sticker]
                }`}
              >
                Edit Order
              </Link>
            </Menu.Item>
          </Menu>
        );

        return (
          <ActionContainer>
            <Dropdown overlay={menu} trigger={['click']}>
              <ThreeDot>
                <EllipsisOutlined />
              </ThreeDot>
            </Dropdown>
          </ActionContainer>
        );
      },
    },
  ];

  if (!isAdmin) {
    return (
      <PageContainer>
        <PageHeader title="Shippo Tracking" />
        <p>Admin access required.</p>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader title="Shippo Tracking" />

      {/* TEMP-MOCK-TRACK UI — commented out for production; restore for Shippo webhook QA.
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="TEMP: Shippo mock track test"
        description="Test-token only. Calls Shippo POST /tracks/ with SHIPPO_* mock numbers, then applies that status to the selected order (dashboard Test labels stay Unknown)."
      />
      <TempTestBar>
        <ExperimentOutlined />
        <Select
          style={{ minWidth: 180 }}
          value={mockStatus}
          options={MockTrackOptions}
          onChange={(value: ShippoMockTrackStatus) => setMockStatus(value)}
        />
        <Button
          type={ButtonType.OUTLINE}
          htmlType="button"
          disabled={selectedRowKeys.length !== 1 || actionLoading}
          onClick={applyMockTrack}
        >
          Apply mock status to selected
        </Button>
        <SelectionHint>
          Select exactly 1 row (use “Đã mua label” queue)
        </SelectionHint>
      </TempTestBar>
      */}

      <Toolbar>
        <Select
          style={{ minWidth: 240 }}
          value={queue}
          options={QueueOptions}
          onChange={(value: QueueView) => {
            setQueue(value);
            setCurrentPage(1);
          }}
        />
        <Select
          style={{ minWidth: 150 }}
          value={statusFilter}
          options={StatusOptions}
          onChange={(value: string) => {
            setStatusFilter(value);
            setCurrentPage(1);
          }}
        />
        <Select
          style={{ minWidth: 200 }}
          mode="multiple"
          allowClear
          placeholder="Seller"
          value={sellerUsername}
          options={sellers?.map((seller) => ({
            label: seller.username,
            value: seller.username,
          }))}
          onChange={(value: string[]) => {
            setSellerUsername(value.length ? value : undefined);
            setCurrentPage(1);
          }}
        />
        <Input.Search
          allowClear
          placeholder="Search order / tracking / address"
          onSearch={(value) => {
            setSearch(value);
            setCurrentPage(1);
            fetchOrders(1, perPage, value, queue);
          }}
          style={{ width: 320 }}
        />
        <Button
          type={ButtonType.OUTLINE}
          htmlType="button"
          onClick={() => fetchOrders()}
        >
          Refresh
        </Button>
        <Button
          type={ButtonType.OUTLINE}
          htmlType="button"
          disabled={actionLoading}
          onClick={syncShippoOrders}
        >
          <ExportOutlined /> Sync Shippo
        </Button>
        <Button
          type={ButtonType.PRIMARY}
          htmlType="button"
          disabled={!canBuySelectedShippoLabels || actionLoading}
          onClick={bulkPurchaseShippoShipments}
        >
          <ShoppingCartOutlined /> Buy Shippo Labels
          {selectedRowKeys.length > 0 ? ` (${selectedRowKeys.length})` : ''}
        </Button>
        {selectedRowKeys.length > 0 && (
          <SelectionHint>{selectedRowKeys.length} selected</SelectionHint>
        )}
      </Toolbar>

      <OrderTable
        columns={columns as any}
        dataSource={itemsPagination.items}
        scroll={{ x: 1800 }}
        rowSelection={rowSelection}
        pagination={{
          current: currentPage,
          pageSize: perPage,
          total: itemsPagination.total || 0,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: (page, pageSize) => {
            setCurrentPage(page);
            setPerPage(pageSize || 20);
          },
        }}
        rowKey={(record) => record?.id}
        bordered={false}
      />

      {(loading || actionLoading) && (
        <SpinContainer>
          <Spin size="large" />
        </SpinContainer>
      )}
    </PageContainer>
  );
};

export default ShippoTracking;
